import { db } from '../firebase/config';
import { doc, updateDoc, addDoc, collection, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

/**
 * PaymentCalculator - Service for calculating and managing contractor payments
 */
class PaymentCalculator {
    
    /**
     * Calculate payment for a job based on contractor's hourly rate and hours worked
     * @param {string} jobId - Work order ID
     * @param {number} hoursWorked - Hours spent on job
     * @returns {Object} Payment calculation details
     */
    async calculateJobPayment(jobId, hoursWorked) {
        try {
            // Get job details
            const jobDoc = await getDoc(doc(db, 'workOrders', jobId));
            if (!jobDoc.exists()) {
                throw new Error('Job not found');
            }

            const job = jobDoc.data();
            const contractorId = job.contractorId;

            if (!contractorId) {
                throw new Error('No contractor assigned to this job');
            }

            // Get contractor details
            const contractorDoc = await getDoc(doc(db, 'contractors', contractorId));
            if (!contractorDoc.exists()) {
                throw new Error('Contractor not found');
            }

            const contractor = contractorDoc.data();
            const hourlyRate = contractor.hourlyRate || 18;

            // Calculate payment
            const calculatedAmount = hourlyRate * hoursWorked;

            return {
                jobId,
                contractorId,
                contractorName: contractor.name,
                hourlyRate,
                hoursWorked,
                calculatedAmount,
                jobType: job.serviceType,
                jobLocation: job.location
            };

        } catch (error) {
            console.error('Error calculating job payment:', error);
            throw error;
        }
    }

    /**
     * Calculate payment using fixed amount (not hourly)
     * @param {string} jobId - Work order ID
     * @param {number} fixedAmount - Fixed payment amount
     * @returns {Object} Payment calculation details
     */
    async calculateFixedPayment(jobId, fixedAmount) {
        try {
            // Get job details
            const jobDoc = await getDoc(doc(db, 'workOrders', jobId));
            if (!jobDoc.exists()) {
                throw new Error('Job not found');
            }

            const job = jobDoc.data();
            const contractorId = job.contractorId;

            if (!contractorId) {
                throw new Error('No contractor assigned to this job');
            }

            // Get contractor details
            const contractorDoc = await getDoc(doc(db, 'contractors', contractorId));
            if (!contractorDoc.exists()) {
                throw new Error('Contractor not found');
            }

            const contractor = contractorDoc.data();

            return {
                jobId,
                contractorId,
                contractorName: contractor.name,
                paymentType: 'fixed',
                calculatedAmount: fixedAmount,
                jobType: job.serviceType,
                jobLocation: job.location
            };

        } catch (error) {
            console.error('Error calculating fixed payment:', error);
            throw error;
        }
    }

    /**
     * Create a payment record for a contractor
     * @param {Object} paymentData - Payment details
     * @returns {string} Payment ID
     */
    async createPayment(paymentData) {
        try {
            const payment = {
                ...paymentData,
                status: 'pending', // pending, approved, paid
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                approvedAt: null,
                approvedBy: null,
                paidAt: null,
                paidBy: null
            };

            // Create payment record
            const paymentRef = await addDoc(collection(db, 'contractorPayments'), payment);

            // Update job with payment info
            if (paymentData.jobId) {
                await updateDoc(doc(db, 'workOrders', paymentData.jobId), {
                    contractorPayment: paymentData.calculatedAmount,
                    contractorPaymentId: paymentRef.id,
                    contractorPaymentStatus: 'pending',
                    updatedAt: new Date().toISOString()
                });
            }

            return paymentRef.id;

        } catch (error) {
            console.error('Error creating payment:', error);
            throw error;
        }
    }

    /**
     * Approve a payment (admin only)
     * @param {string} paymentId - Payment ID
     * @param {string} adminUid - Admin user ID
     * @param {string} note - Optional approval note
     */
    async approvePayment(paymentId, adminUid, note = '') {
        try {
            const paymentRef = doc(db, 'contractorPayments', paymentId);
            const paymentDoc = await getDoc(paymentRef);

            if (!paymentDoc.exists()) {
                throw new Error('Payment not found');
            }

            const payment = paymentDoc.data();

            // Update payment status
            await updateDoc(paymentRef, {
                status: 'approved',
                approvedAt: new Date().toISOString(),
                approvedBy: adminUid,
                approvalNote: note,
                updatedAt: new Date().toISOString()
            });

            // Update job status if linked
            if (payment.jobId) {
                await updateDoc(doc(db, 'workOrders', payment.jobId), {
                    contractorPaymentStatus: 'approved',
                    updatedAt: new Date().toISOString()
                });
            }

            console.log('✅ Payment approved:', paymentId);

        } catch (error) {
            console.error('Error approving payment:', error);
            throw error;
        }
    }

    /**
     * Mark payment as paid
     * @param {string} paymentId - Payment ID
     * @param {string} adminUid - Admin user ID
     * @param {string} paymentMethod - How it was paid (e.g., "Stripe", "Check", "Direct Deposit")
     * @param {string} note - Optional payment note
     */
    async markAsPaid(paymentId, adminUid, paymentMethod = 'Manual', note = '') {
        try {
            const paymentRef = doc(db, 'contractorPayments', paymentId);
            const paymentDoc = await getDoc(paymentRef);

            if (!paymentDoc.exists()) {
                throw new Error('Payment not found');
            }

            const payment = paymentDoc.data();

            // Update payment status
            await updateDoc(paymentRef, {
                status: 'paid',
                paidAt: new Date().toISOString(),
                paidBy: adminUid,
                paymentMethod,
                paymentNote: note,
                updatedAt: new Date().toISOString()
            });

            // Update job status if linked
            if (payment.jobId) {
                await updateDoc(doc(db, 'workOrders', payment.jobId), {
                    contractorPaid: true,
                    contractorPaymentStatus: 'paid',
                    updatedAt: new Date().toISOString()
                });
            }

            console.log('✅ Payment marked as paid:', paymentId);

        } catch (error) {
            console.error('Error marking payment as paid:', error);
            throw error;
        }
    }

    /**
     * Get all payments for a contractor
     * @param {string} contractorId - Contractor ID
     * @returns {Array} List of payments
     */
    async getContractorPayments(contractorId) {
        try {
            const q = query(
                collection(db, 'contractorPayments'),
                where('contractorId', '==', contractorId)
            );

            const snapshot = await getDocs(q);
            const payments = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by date (newest first)
            payments.sort((a, b) => 
                new Date(b.createdAt) - new Date(a.createdAt)
            );

            return payments;

        } catch (error) {
            console.error('Error getting contractor payments:', error);
            throw error;
        }
    }

    /**
     * Get payment statistics for a contractor
     * @param {string} contractorId - Contractor ID
     * @returns {Object} Payment stats
     */
    async getPaymentStats(contractorId) {
        try {
            const payments = await this.getContractorPayments(contractorId);

            const stats = {
                totalEarnings: 0,
                pendingPayments: 0,
                approvedPayments: 0,
                paidPayments: 0,
                totalPaid: 0,
                totalPending: 0,
                paymentCount: payments.length
            };

            payments.forEach(payment => {
                const amount = payment.calculatedAmount || 0;

                stats.totalEarnings += amount;

                if (payment.status === 'pending') {
                    stats.pendingPayments++;
                    stats.totalPending += amount;
                } else if (payment.status === 'approved') {
                    stats.approvedPayments++;
                    stats.totalPending += amount;
                } else if (payment.status === 'paid') {
                    stats.paidPayments++;
                    stats.totalPaid += amount;
                }
            });

            return stats;

        } catch (error) {
            console.error('Error getting payment stats:', error);
            throw error;
        }
    }

    /**
     * Get all pending payments (admin view)
     * @returns {Array} List of pending payments
     */
    async getAllPendingPayments() {
        try {
            const q = query(
                collection(db, 'contractorPayments'),
                where('status', 'in', ['pending', 'approved'])
            );

            const snapshot = await getDocs(q);
            const payments = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by date (oldest first for pending payments)
            payments.sort((a, b) => 
                new Date(a.createdAt) - new Date(b.createdAt)
            );

            return payments;

        } catch (error) {
            console.error('Error getting pending payments:', error);
            throw error;
        }
    }

    /**
     * Delete a payment (admin only)
     * @param {string} paymentId - Payment ID
     */
    async deletePayment(paymentId) {
        try {
            const paymentRef = doc(db, 'contractorPayments', paymentId);
            const paymentDoc = await getDoc(paymentRef);

            if (!paymentDoc.exists()) {
                throw new Error('Payment not found');
            }

            const payment = paymentDoc.data();

            // Remove from Firestore
            await deleteDoc(paymentRef);

            // Update job if linked
            if (payment.jobId) {
                await updateDoc(doc(db, 'workOrders', payment.jobId), {
                    contractorPayment: null,
                    contractorPaymentId: null,
                    contractorPaymentStatus: null,
                    contractorPaid: false,
                    updatedAt: new Date().toISOString()
                });
            }

            console.log('✅ Payment deleted:', paymentId);

        } catch (error) {
            console.error('Error deleting payment:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const paymentCalculator = new PaymentCalculator();
